import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Activity, Flame, GraduationCap, Loader2, Mail, Lock, AtSign } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import { api } from '../lib/api';

const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.38-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.38C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FieldIcon = ({ icon: Icon }) => (
  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none"><Icon size={15} /></span>
);

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const [mode, setMode] = useState('signin'); // signin | signup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const errorText = (err) => {
    const detail = err.response?.data?.detail;
    const code = typeof detail === 'object' ? detail?.code : null;
    const map = {
      INVALID_CREDENTIALS: t('authInvalidCredentials'),
      EMAIL_EXISTS: t('authEmailExists'),
      USE_GOOGLE: t('authUseGoogle'),
      USERNAME_TAKEN: t('usernameTaken'),
      INVALID_USERNAME: t('usernameHint'),
      WEAK_PASSWORD: t('passwordHint'),
      INVALID_EMAIL: t('authInvalidCredentials'),
    };
    return map[code] || (typeof detail === 'string' ? detail : t('authInvalidCredentials'));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data } = mode === 'signup'
        ? await api.post('/auth/register', { email: email.trim(), password, username: username.trim() })
        : await api.post('/auth/login', { email: email.trim(), password });
      setUser(data.user); // instant landing on Dashboard via <Navigate>
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const benefits = [
    { icon: Activity, text: t('benefit1') },
    { icon: Flame, text: t('benefit2') },
    { icon: GraduationCap, text: t('benefit3') },
  ];

  const inputCls = 'w-full h-12 rounded-[14px] bg-white/[0.05] border border-white/10 pl-10 pr-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none';

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" style={{ background: 'linear-gradient(180deg, #121212 0%, #181818 60%, #141414 100%)' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(700px circle at 15% 5%, rgba(34,197,94,0.08), transparent 55%)' }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/33210166/pexels-photo-33210166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5 max-w-6xl w-full mx-auto">
        <div className="flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="SkillSync AI" className="w-11 h-11 object-contain" draggable={false} data-testid="login-logo" />
          <div>
            <div className="font-display font-bold tracking-tight leading-none text-white">SkillSync <span className="text-[#22C55E]">AI</span></div>
            <div className="font-mono text-[10px] text-white/45 mt-0.5">{t('tagline')}</div>
          </div>
        </div>
        <div className="flex items-center rounded-[14px] border border-white/10 bg-white/[0.04] p-0.5" data-testid="language-toggle">
          {['en', 'it'].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              data-testid={`language-toggle-${l}`}
              className={`px-2.5 py-1 rounded-[12px] text-xs font-mono uppercase transition-colors ${
                lang === l ? 'bg-white/[0.14] text-white border border-white/25' : 'text-white/50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 sm:px-8 py-8">
        <div className="max-w-5xl w-full grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: 'easeOut' }} className="text-left">
            <div className="font-mono text-xs text-white/50 tracking-widest uppercase mb-4">/// SKILLSYNC ECOSYSTEM</div>
            <h1 className="text-4xl sm:text-6xl font-display font-extrabold tracking-tight leading-[0.98] mb-4 text-white">
              {t('loginHero')}
            </h1>
            <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-8">{t('loginSub')}</p>
            <ul className="space-y-3">
              {benefits.map(({ icon: Icon, text }, i) => (
                <motion.li
                  key={text}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.12 + i * 0.08 }}
                  className="flex items-center gap-3 text-sm text-white/85"
                >
                  <span className="w-8 h-8 rounded-[14px] bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-white/70" />
                  </span>
                  {text}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* auth card */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1, ease: 'easeOut' }} className="ss-card p-6 sm:p-7 w-full max-w-md lg:ml-auto" data-testid="auth-card">
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="auth-mode-tabs">
              <button
                onClick={() => { setMode('signin'); setError(null); }}
                data-testid="auth-tab-signin"
                className={`ss-chip h-10 text-sm font-medium ${mode === 'signin' ? 'ss-chip-active' : ''}`}
              >
                {t('signIn')}
              </button>
              <button
                onClick={() => { setMode('signup'); setError(null); }}
                data-testid="auth-tab-signup"
                className={`ss-chip h-10 text-sm font-medium ${mode === 'signup' ? 'ss-chip-active' : ''}`}
              >
                {t('signUp')}
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
              {mode === 'signup' && (
                <div className="relative">
                  <FieldIcon icon={AtSign} />
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('usernameLabel')}
                    maxLength={20}
                    required
                    data-testid="auth-username-input"
                    className={inputCls}
                  />
                </div>
              )}
              <div className="relative">
                <FieldIcon icon={Mail} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailLabel')}
                  required
                  data-testid="auth-email-input"
                  className={inputCls}
                />
              </div>
              <div className="relative">
                <FieldIcon icon={Lock} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('passwordLabel')}
                  minLength={6}
                  required
                  data-testid="auth-password-input"
                  className={inputCls}
                />
              </div>
              {mode === 'signup' && <p className="font-mono text-[10px] text-white/35 px-1">{t('usernameHint')} · {t('passwordHint')}</p>}

              {error && (
                <p className="text-xs text-[#E76F7B] bg-[rgba(231,111,123,0.08)] border border-[rgba(231,111,123,0.25)] rounded-[12px] px-3 py-2.5" data-testid="auth-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-button"
                className="ss-btn-primary w-full h-12 font-display font-bold text-sm flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                {mode === 'signup' ? t('createAccount') : t('signIn')}
              </button>
            </form>

            <div className="flex items-center gap-3 my-4">
              <span className="flex-1 h-px bg-white/[0.08]" />
              <span className="font-mono text-[10px] uppercase text-white/35">{t('orDivider')}</span>
              <span className="flex-1 h-px bg-white/[0.08]" />
            </div>

            <div className="space-y-2.5">
              <button
                onClick={handleGoogleLogin}
                data-testid="login-google-button"
                className="ss-btn-glass w-full h-12 font-medium text-sm flex items-center justify-center gap-3"
              >
                <GoogleIcon /> {t('signInGoogle')}
              </button>
              <button
                disabled
                data-testid="login-apple-button"
                className="ss-btn-glass w-full h-12 font-medium text-sm flex items-center justify-center gap-3 opacity-50 cursor-not-allowed relative"
                title={t('comingSoon')}
              >
                <AppleIcon /> {t('continueWithApple')}
                <span className="absolute right-3 font-mono text-[9px] uppercase tracking-wide text-white/60 border border-white/20 rounded-[8px] px-1.5 py-0.5" data-testid="apple-coming-soon-badge">
                  {t('comingSoon')}
                </span>
              </button>
            </div>

            <p className="text-xs text-white/35 font-mono mt-4 text-center">{t('legalNote')}</p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
